<template>
  <div id="createOrganizationPromptModal" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Create Organization Prompt</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" :disabled="saving"></button>
        </div>
        <div class="modal-body">
          <div v-if="saving" class="d-flex flex-column align-items-center justify-content-center p-5" style="min-height: 200px;">
            <div class="spinner-border text-primary mb-3" role="status">
              <span class="visually-hidden">Creating...</span>
            </div>
            <div class="text-muted">Creating organization prompt...</div>
          </div>

          <div v-else>
            <div class="mb-3">
              <label class="form-label fw-bold">Customer</label>
              <select
                class="form-select"
                v-model="selectedCustomerId"
                :disabled="saving"
                @change="onCustomerChange"
              >
                <option value="">Select a customer...</option>
                <option v-for="customer in customers" :key="customer.customerId" :value="customer.customerId">
                  {{ customer.customerName }}
                </option>
              </select>
            </div>

            <template v-if="selectedCustomerId && selectedCustomerHasPrompts">
              <div v-if="loadingHistory" class="mb-3 text-muted small">
                Loading versions...
              </div>
              <template v-else>
                <div class="mb-3">
                  <div class="form-label fw-bold">How do you want to create this prompt?</div>
                  <div class="form-check">
                    <input
                      id="source-existing"
                      v-model="sourceChoice"
                      type="radio"
                      value="existing"
                      class="form-check-input"
                      :disabled="saving"
                    />
                    <label for="source-existing" class="form-check-label">Edit from existing version</label>
                  </div>
                  <div class="form-text text-muted mb-2">Load an existing version and save as a new version.</div>
                  <div v-if="sourceChoice === 'existing'" class="ms-4 mt-2">
                    <label class="form-label small fw-bold">Base new version on:</label>
                    <select
                      class="form-select form-select-sm"
                      v-model="selectedVersionPromptId"
                      :disabled="saving"
                      @change="onVersionSelect"
                    >
                      <option
                        v-for="(p, idx) in historySorted"
                        :key="p.promptId"
                        :value="p.promptId"
                      >
                        {{ versionOptionLabel(p, idx) }}
                      </option>
                    </select>
                  </div>
                  <div class="form-check mt-3">
                    <input
                      id="source-fresh"
                      v-model="sourceChoice"
                      type="radio"
                      value="fresh"
                      class="form-check-input"
                      :disabled="saving"
                    />
                    <label for="source-fresh" class="form-check-label">Start fresh</label>
                  </div>
                  <div class="form-text text-muted">Create a new version with new content.</div>
                </div>
              </template>
            </template>

            <div class="mb-3">
              <label class="form-label fw-bold">Prompt Content</label>
              <textarea
                class="form-control"
                v-model="promptContent"
                rows="15"
                placeholder="Enter the organization prompt content..."
                :disabled="saving"
              ></textarea>
              <div class="form-text">
                <template v-if="!selectedCustomerHasPrompts">
                  This will create the first version (1.0.0) of the organization prompt for the selected customer.
                </template>
                <template v-else-if="sourceChoice === 'existing'">
                  Load an existing version and save as a new version.
                </template>
                <template v-else>
                  Create a new version with new content.
                </template>
              </div>
            </div>

            <div v-if="errorMessage" class="alert alert-danger" role="alert">
              {{ errorMessage }}
            </div>
          </div>
        </div>
        <div class="modal-footer" v-if="!saving">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="saving">Cancel</button>
          <button type="button" class="btn btn-primary" @click="onReview" :disabled="!canSubmit">
            Review
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InsightsService from '../services/InsightsService.js';
import dayjs from 'dayjs';

function semverCompare(a, b) {
  const pa = (a || '').toString().split('.').map(Number);
  const pb = (b || '').toString().split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const na = pa[i] || 0;
    const nb = pb[i] || 0;
    if (na > nb) return 1;
    if (na < nb) return -1;
  }
  return 0;
}

export default {
  name: 'CreateOrganizationPromptModal',
  props: {
    show: Boolean,
    draft: {
      type: Object,
      default: null
    },
    customers: {
      type: Array,
      default: () => []
    },
    customersWithPrompts: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedCustomerId: '',
      promptContent: '',
      saving: false,
      errorMessage: '',
      insightsService: new InsightsService(),
      model: streetmetrics.model,
      currentModal: null,
      modalElement: null,
      history: [],
      loadingHistory: false,
      sourceChoice: 'existing',
      selectedVersionPromptId: '',
      _hiddenModalHandler: null
    };
  },
  computed: {
    selectedCustomerHasPrompts() {
      if (!this.selectedCustomerId || !this.customersWithPrompts.length) return false;
      return this.customersWithPrompts.some(c => c.customerId === parseInt(this.selectedCustomerId, 10));
    },
    historySorted() {
      if (!this.history.length) return [];
      return [...this.history].sort((a, b) => semverCompare(b.version, a.version));
    },
    canSubmit() {
      return this.selectedCustomerId && this.promptContent.trim().length > 1;
    }
  },
  watch: {
    show: {
      async handler(val) {
        if (val) {
          if (this.draft && this.draft.customerId) {
            this.selectedCustomerId = String(this.draft.customerId);
            this.promptContent = this.draft.promptContent ?? '';
            this.sourceChoice = this.draft.sourceChoice ?? 'existing';
            this.selectedVersionPromptId =
              this.draft.selectedVersionPromptId !== undefined && this.draft.selectedVersionPromptId !== null
                ? this.draft.selectedVersionPromptId
                : '';
            if (this.draft.sourceChoice === 'existing' && this.draft.selectedVersionPromptId != null) {
              await this.loadHistory(true);
            }
          }
          this.openModal();
        } else {
          this.closeModal();
          this.resetForm();
        }
      },
      immediate: true
    },
    sourceChoice(val) {
      if (val === 'fresh') {
        this.promptContent = '';
      } else if (val === 'existing' && this.historySorted.length > 0) {
        const p = this.historySorted.find(h => h.promptId === this.selectedVersionPromptId) || this.historySorted[0];
        this.selectedVersionPromptId = p.promptId;
        this.promptContent = p.promptContent || '';
      }
    }
  },
  mounted() {
    this.modalElement = document.getElementById('createOrganizationPromptModal');
    this.currentModal = new bootstrap.Modal(this.modalElement);
    if (this.show) {
      this.currentModal.show();
    }
    this._hiddenModalHandler = () => {
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) backdrop.remove();
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      this.$emit('close');
    };
    this.modalElement.addEventListener('hidden.bs.modal', this._hiddenModalHandler);
  },
  beforeDestroy() {
    if (this.modalElement && this._hiddenModalHandler) {
      this.modalElement.removeEventListener('hidden.bs.modal', this._hiddenModalHandler);
    }
  },
  methods: {
    formatDate(dateStr) {
      return dayjs(dateStr).format('MMM D, YYYY');
    },
    versionOptionLabel(p, index) {
      const version = p.version || '—';
      const active = p.isActive ? ' (active)' : '';
      const date = p.createdAt ? ` – ${this.formatDate(p.createdAt)}` : '';
      const latest = index === 0 ? ' (latest)' : '';
      return `${version}${active}${date}${latest}`;
    },
    async onCustomerChange() {
      this.promptContent = '';
      this.sourceChoice = 'existing';
      this.selectedVersionPromptId = '';
      this.history = [];
      if (!this.selectedCustomerId || !this.selectedCustomerHasPrompts) return;
      await this.loadHistory();
    },
    async loadHistory(preserveSelections = false) {
      if (!this.selectedCustomerId) return;
      this.loadingHistory = true;
      this.history = [];
      try {
        const raw = await this.insightsService.getOrganizationPromptHistory(parseInt(this.selectedCustomerId, 10));
        this.history = raw || [];
        const sorted = [...this.history].sort((a, b) => semverCompare(b.version, a.version));
        if (sorted.length > 0 && !preserveSelections) {
          this.selectedVersionPromptId = sorted[0].promptId;
          this.promptContent = sorted[0].promptContent || '';
        }
      } catch (err) {
        console.error('Error loading prompt history:', err);
        this.errorMessage = 'Failed to load versions.';
      } finally {
        this.loadingHistory = false;
      }
    },
    onVersionSelect() {
      const p = this.historySorted.find(h => h.promptId === this.selectedVersionPromptId);
      this.promptContent = p ? (p.promptContent || '') : '';
    },
    openModal() {
      if (this.currentModal) {
        this.currentModal.show();
      }
    },
    closeModal() {
      if (this.currentModal) {
        this.currentModal.hide();
      }
    },
    resetForm() {
      this.selectedCustomerId = '';
      this.promptContent = '';
      this.errorMessage = '';
      this.saving = false;
      this.history = [];
      this.loadingHistory = false;
      this.sourceChoice = 'existing';
      this.selectedVersionPromptId = '';
    },
    onReview() {
      if (!this.canSubmit) return;
      const customerId = parseInt(this.selectedCustomerId, 10);
      const customer = this.customers.find(c => c.customerId === customerId);
      const draft = {
        customerId,
        promptContent: this.promptContent.trim(),
        customerName: customer ? customer.customerName : null,
        sourceChoice: this.sourceChoice,
        selectedVersionPromptId: this.selectedVersionPromptId || undefined
      };
      if (this.sourceChoice === 'existing' && this.selectedVersionPromptId && this.historySorted.length) {
        const base = this.historySorted.find(p => p.promptId === this.selectedVersionPromptId);
        draft.previousContent = base?.promptContent ?? '';
      }
      this.$emit('review', draft);
    }
  }
};
</script>

<style scoped>
.form-label {
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

textarea.form-control {
  font-family: monospace;
  font-size: 0.9rem;
}
</style>
